import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createLabCase, getLabCaseDetail, getLabCases, updateLabCase } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabEmptyState from '../components/LabEmptyState';
import LabTable from '../components/LabTable';
import SmartiLabBadge from '../components/SmartiLabBadge';

const resolutionTypes = ['process_issue', 'station_issue', 'spec_issue', 'software_version_issue', 'operator_training_issue', 'material_component_issue', 'false_positive', 'no_fault_found', 'other'];
const verificationResults = ['not_verified', 'improved', 'no_change', 'regressed', 'needs_more_data'];

const initialForm = {
  title: '',
  priority: 'medium',
  status: 'open',
  linked_entity_type: 'serial',
  linked_entity_key: '',
  assigned_to_username: '',
  due_at: '',
  suspected_root_cause: '',
  resolution_type: '',
  corrective_action: '',
  verification_result: 'not_verified',
  review_status: 'unreviewed',
  notes: '',
};

function titleize(value) {
  return String(value || '—').replace(/_/g, ' ');
}

export default function CasesPage() {
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (keepId = selectedId) => {
    const cases = await getLabCases();
    setRows(cases);
    const nextId = keepId || cases[0]?.id || null;
    setSelectedId(nextId);
    if (nextId) {
      setSelectedCase(await getLabCaseDetail(nextId));
    } else {
      setSelectedCase(null);
    }
  }, [selectedId]);

  useEffect(() => {
    load().catch((loadError) => setError(loadError.message || 'Unable to load cases.'));
  }, [load]);

  const openCases = useMemo(() => rows.filter((row) => row.status !== 'closed'), [rows]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await createLabCase(form);
      setForm(initialForm);
      await load(created?.id);
    } catch (createError) {
      setError(createError.message || 'Could not create the case.');
    } finally {
      setSaving(false);
    }
  };

  const handlePatch = async (patch) => {
    if (!selectedId) return;
    setSaving(true);
    setError('');
    try {
      await updateLabCase(selectedId, patch);
      await load(selectedId);
    } catch (patchError) {
      setError(patchError.message || 'Could not update the case.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lab-page">
      <LabCard title="Operational case workflow" description="Create, assign, verify, and close investigations with a clear audit trail. This page turns Smarti Lab cases into a real operational loop instead of a note list.">
        {error ? <div className="lab-error-text">{error}</div> : null}
        <div className="lab-metric-grid">
          <div className="lab-human-action-panel"><strong>Open investigations</strong><p>{openCases.length}</p></div>
          <div className="lab-human-action-panel"><strong>Closed cases</strong><p>{rows.filter((row) => row.status === 'closed').length}</p></div>
          <div className="lab-human-action-panel"><strong>Critical or high</strong><p>{rows.filter((row) => ['critical', 'high'].includes(row.priority)).length}</p></div>
        </div>
      </LabCard>

      <div className="lab-grid two-col">
        <LabCard title="Create case" description="Cases can start from the cockpit, suspect pages, sessions, drift alerts, or manual investigation work here.">
          <form className="lab-form-grid" onSubmit={handleCreate}>
            <input className="lab-input" placeholder="Case title" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} required />
            <select className="lab-input" value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select className="lab-input" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="waiting">Waiting</option>
              <option value="closed">Closed</option>
            </select>
            <select className="lab-input" value={form.linked_entity_type} onChange={(event) => setForm((current) => ({ ...current, linked_entity_type: event.target.value }))}>
              <option value="serial">Serial</option>
              <option value="session">Session</option>
              <option value="station">Station</option>
              <option value="drift">Drift</option>
              <option value="suspect">Suspect</option>
              <option value="quality">Quality blocker</option>
            </select>
            <input className="lab-input" placeholder="Linked entity key" value={form.linked_entity_key} onChange={(event) => setForm((current) => ({ ...current, linked_entity_key: event.target.value }))} />
            <input className="lab-input" placeholder="Owner username" value={form.assigned_to_username} onChange={(event) => setForm((current) => ({ ...current, assigned_to_username: event.target.value }))} />
            <input className="lab-input" type="date" value={form.due_at} onChange={(event) => setForm((current) => ({ ...current, due_at: event.target.value }))} />
            <input className="lab-input" placeholder="Suspected root cause" value={form.suspected_root_cause} onChange={(event) => setForm((current) => ({ ...current, suspected_root_cause: event.target.value }))} />
            <select className="lab-input" value={form.resolution_type} onChange={(event) => setForm((current) => ({ ...current, resolution_type: event.target.value }))}>
              <option value="">Resolution type</option>
              {resolutionTypes.map((value) => <option key={value} value={value}>{titleize(value)}</option>)}
            </select>
            <select className="lab-input" value={form.verification_result} onChange={(event) => setForm((current) => ({ ...current, verification_result: event.target.value }))}>
              {verificationResults.map((value) => <option key={value} value={value}>{titleize(value)}</option>)}
            </select>
            <textarea className="lab-input" placeholder="Corrective action" value={form.corrective_action} onChange={(event) => setForm((current) => ({ ...current, corrective_action: event.target.value }))} />
            <textarea className="lab-input" placeholder="Notes / opening reason" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
            <button type="submit" className="lab-button" disabled={saving}>{saving ? 'Saving...' : 'Create case'}</button>
          </form>
        </LabCard>

        <LabCard title="Case list" description="Open a case to review owner, due date, corrective action, verification result, linked evidence, and the event history.">
          <LabTable
            rows={rows}
            emptyTitle="No cases yet."
            emptyDescription="Create the first operational investigation case from the form or from the cockpit."
            columns={[
              { key: 'title', label: 'Title', render: (value, row) => <button type="button" className="lab-inline-button" onClick={() => load(row.id)}>{value}</button> },
              { key: 'status', label: 'Status', render: (value) => <SmartiLabBadge variant={value === 'closed' ? 'success' : value === 'investigating' ? 'warning' : 'primary'} label={value} /> },
              { key: 'priority', label: 'Priority' },
              { key: 'assigned_to_username', label: 'Owner' },
              { key: 'due_at', label: 'Due date' },
              { key: 'updated_at', label: 'Updated' },
            ]}
          />
        </LabCard>
      </div>

      <LabCard title={selectedCase ? `Case detail: ${selectedCase.title}` : 'Case detail'} description="The detail view keeps closure paths explicit: owner, due date, resolution type, verification result, and event history all stay visible.">
        {!selectedCase ? (
          <LabEmptyState title="Select a case" description="Choose any case from the list to inspect its workflow and evidence context." />
        ) : (
          <div className="lab-grid two-col">
            <div className="lab-form-grid">
              <select className="lab-input" value={selectedCase.status || 'open'} onChange={(event) => handlePatch({ status: event.target.value })}>
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="waiting">Waiting</option>
                <option value="closed">Closed</option>
              </select>
              <select className="lab-input" value={selectedCase.priority || 'medium'} onChange={(event) => handlePatch({ priority: event.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
              <input className="lab-input" value={selectedCase.assigned_to_username || ''} onChange={(event) => setSelectedCase((current) => ({ ...current, assigned_to_username: event.target.value }))} onBlur={() => handlePatch({ assigned_to_username: selectedCase.assigned_to_username || '' })} placeholder="Owner username" />
              <input className="lab-input" type="date" value={(selectedCase.due_at || '').slice(0, 10)} onChange={(event) => handlePatch({ due_at: event.target.value })} />
              <select className="lab-input" value={selectedCase.resolution_type || ''} onChange={(event) => handlePatch({ resolution_type: event.target.value })}>
                <option value="">Resolution type</option>
                {resolutionTypes.map((value) => <option key={value} value={value}>{titleize(value)}</option>)}
              </select>
              <select className="lab-input" value={selectedCase.verification_result || 'not_verified'} onChange={(event) => handlePatch({ verification_result: event.target.value })}>
                {verificationResults.map((value) => <option key={value} value={value}>{titleize(value)}</option>)}
              </select>
              <select className="lab-input" value={selectedCase.review_status || 'unreviewed'} onChange={(event) => handlePatch({ review_status: event.target.value })}>
                <option value="unreviewed">Unreviewed</option>
                <option value="reviewed">Reviewed</option>
                <option value="verified">Verified</option>
              </select>
              <textarea className="lab-input" value={selectedCase.corrective_action || ''} onChange={(event) => setSelectedCase((current) => ({ ...current, corrective_action: event.target.value }))} onBlur={() => handlePatch({ corrective_action: selectedCase.corrective_action || '' })} placeholder="Corrective action" />
              <textarea className="lab-input" value={selectedCase.notes || ''} onChange={(event) => setSelectedCase((current) => ({ ...current, notes: event.target.value }))} onBlur={() => handlePatch({ notes: selectedCase.notes || '' })} placeholder="Working notes" />
            </div>

            <div className="lab-case-grid">
              <article className="lab-compare-card">
                <strong>Linked evidence</strong>
                <p>{titleize(selectedCase.linked_entity_type)}: {selectedCase.linked_entity_key || '—'}</p>
                <p>{selectedCase.suspected_root_cause || 'No suspected cause recorded yet.'}</p>
                {selectedCase.linked_entity_type === 'suspect' && selectedCase.linked_entity_key ? (
                  <Link className="lab-inline-button" to={`/smarti-lab/suspects/${encodeURIComponent(selectedCase.linked_entity_key)}`}>Open suspect page</Link>
                ) : null}
                {selectedCase.linked_entity_type === 'serial' && selectedCase.linked_entity_key ? (
                  <Link className="lab-inline-button" to={`/smarti-lab/units/${encodeURIComponent(selectedCase.linked_entity_key)}`}>Open session story</Link>
                ) : null}
              </article>

              <article className="lab-compare-card">
                <strong>Activity log</strong>
                {(selectedCase.events || []).length ? (selectedCase.events || []).map((event) => (
                  <p key={event.id}><strong>{titleize(event.event_type)}</strong> • {event.created_at}<br />{event.body || 'No event body'}</p>
                )) : <p>No activity yet.</p>}
              </article>

              {selectedCase.feedback?.length ? (
                <article className="lab-compare-card">
                  <strong>Related suspect feedback</strong>
                  {selectedCase.feedback.map((item) => (
                    <p key={item.id}><strong>{titleize(item.feedback_label)}</strong> • {item.owner_username}<br />{item.feedback_note || 'No note'} </p>
                  ))}
                </article>
              ) : null}

              {selectedCase.links?.length ? (
                <article className="lab-compare-card">
                  <strong>Related link evidence</strong>
                  {selectedCase.links.slice(0, 4).map((item) => (
                    <p key={`${item.serialNumber}-${item.burnInSourceLabel || 'missing'}`}>
                      {item.serialNumber} -> {item.burnInSourceLabel || 'No burn-in link'}
                      <br />
                      {item.statusBucket} • confidence {Number(item.confidence || 0).toFixed(2)}
                    </p>
                  ))}
                </article>
              ) : null}
            </div>
          </div>
        )}
      </LabCard>
    </div>
  );
}
